import React from 'react';
import styles from './styles';
import { colors } from './theme';
import { IconSettings } from './feedIcons';
import { usablePhotoURL } from '../lib/avatarPhoto';

/**
 * RW-2963: the right end of the app header — the admin Admin/Agent view pill
 * and the avatar (account) menu.
 *
 * At a 320px viewport the header's right cluster was wider than the screen
 * for an admin, and the avatar ended past the edge (x≈346). On phones
 * (the app's `mobile` breakpoint, max-width 900px) the view switch now lives
 * inside the avatar menu instead of beside it; desktop keeps the pill.
 * Both read and write the same ViewModeContext value through the props.
 */

// The cluster's gap, shared with the width budget below so the two can't drift.
export const HEADER_CLUSTER_GAP = 6;

// The header's right cluster. minWidth 0 + flexShrink 1 let it give way to the
// viewport instead of pushing the page wider; justifyContent flex-end keeps
// its LAST item (the avatar) flush with the right edge, so if the controls
// ever outgrow a tiny screen again they spill to the left, under the title,
// and the account control stays reachable. When everything fits — every
// desktop width — the cluster is sized to its content and nothing moves.
export const HEADER_CLUSTER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: HEADER_CLUSTER_GAP,
  minWidth: 0,
  flexShrink: 1,
};

// ── Width budget (pure) ─────────────────────────────────────────────
// Widths of the header's right-cluster controls, from their own styles.
// The bug launcher is portaled to document.body (a floating button), so it
// takes no room in the header.
export const HEADER_SIDE_PADDING = 20; // App.js header `padding: '0 20px'`
export const NOTIFICATION_BELL_WIDTH = 44; // NotificationBell trigger minWidth
// The Admin/Agent pill: two 11px labels with 10px side padding, a 2px gap and
// 2px of inset — about 108px. Consistent with the audit: an admin's avatar
// ended at x≈346 on a 320px screen, i.e. 46px past the 300px content edge.
export const VIEW_PILL_WIDTH = 108;

export function headerClusterWidth({ askReve = true, viewPillInHeader = false } = {}) {
  const icon = styles.headerIconBtn.width; // search, Ask Rêve, Messages
  const widths = [
    icon, // search
    ...(askReve ? [icon] : []),
    icon, // messages
    NOTIFICATION_BELL_WIDTH,
    ...(viewPillInHeader ? [VIEW_PILL_WIDTH] : []),
    styles.avatarBtn.width,
  ];
  return widths.reduce((a, b) => a + b, 0) + HEADER_CLUSTER_GAP * (widths.length - 1);
}

// Room the cluster has at a viewport width (the title may shrink to nothing).
export function headerClusterRoom(viewportWidth) {
  return viewportWidth - 2 * HEADER_SIDE_PADDING;
}

// ── View switch ─────────────────────────────────────────────────────

function ViewModePill({ viewMode, setViewMode }) {
  const pillBtn = (active, activeBg) => ({
    padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: 11,
    fontWeight: 600, cursor: 'pointer', transition: 'background 150ms',
    background: active ? activeBg : 'transparent',
    color: active ? '#fff' : '#6B7280',
    fontFamily: "'Jost', sans-serif",
  });
  return (
    <div
      role="group"
      aria-label="View as"
      style={{
        display: 'flex', alignItems: 'center', gap: 2,
        background: '#F3F4F6', borderRadius: 6, padding: 2,
        fontFamily: "'Jost', sans-serif",
      }}
    >
      <button type="button" aria-pressed={viewMode === 'admin'} onClick={() => setViewMode('admin')} style={pillBtn(viewMode === 'admin', '#6366F1')}>Admin</button>
      <button type="button" aria-pressed={viewMode === 'agent'} onClick={() => setViewMode('agent')} style={pillBtn(viewMode === 'agent', '#EE8172')}>Agent</button>
    </div>
  );
}

const VIEW_OPTIONS = [
  { mode: 'admin', label: 'Admin view' },
  { mode: 'agent', label: 'Agent view' },
];

function ViewModeMenuItems({ viewMode, onPick }) {
  return (
    <div role="group" aria-label="View as" style={{ marginBottom: 10 }}>
      <p aria-hidden="true" style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: colors.muted }}>View as</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {VIEW_OPTIONS.map(({ mode, label }) => {
          const on = viewMode === mode;
          return (
            <button
              key={mode}
              type="button"
              role="menuitemradio"
              aria-checked={on}
              onClick={() => onPick(mode)}
              style={{
                ...styles.userMenuBtn,
                minHeight: 44,
                fontWeight: on ? 600 : 500,
                background: on ? colors.pearlLight : 'transparent',
                border: `1.5px solid ${on ? colors.coral : '#E5E7EB'}`,
                color: on ? colors.coralInk : colors.muted,
              }}
            >
              {on ? <span aria-hidden="true">✓ </span> : null}{label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── The cluster's right end ─────────────────────────────────────────

export default function HeaderAccountMenu({
  user,
  mobile,
  canSwitchView,
  viewMode,
  setViewMode,
  menuOpen,
  setMenuOpen,
  onSettings,
  onLogout,
}) {
  const viewInMenu = canSwitchView && mobile;
  return (
    <>
      {/* ROLE-VIEWS: View switcher pill — only for admins who can toggle;
          on phones it moves into the avatar menu (RW-2963). */}
      {canSwitchView && !mobile ? <ViewModePill viewMode={viewMode} setViewMode={setViewMode} /> : null}

      {/* User Menu */}
      <div style={{ ...styles.avatarWrap, flexShrink: 0 }}>
        <button
          type="button"
          style={styles.avatarBtn}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Account menu"
          aria-expanded={menuOpen}
          aria-haspopup="true"
        >
          <img
            src={usablePhotoURL(user.photoURL) || undefined}
            alt=""
            style={{
              ...styles.navAvatarImg,
              display: usablePhotoURL(user.photoURL) ? 'block' : 'none',
            }} loading="lazy"
          />
          {!usablePhotoURL(user.photoURL) ? (
            <span style={styles.navAvatarFallback}>
              {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
            </span>
          ) : null}
        </button>
        {menuOpen ? (
          <div role="menu" aria-label="User menu" style={styles.userMenu}>
            <p style={styles.userMenuEmail}>{user.email}</p>
            {viewInMenu ? (
              <ViewModeMenuItems
                viewMode={viewMode}
                onPick={(mode) => { setViewMode(mode); setMenuOpen(false); }}
              />
            ) : null}
            <button
              type="button"
              role="menuitem"
              style={styles.userMenuBtn}
              onClick={() => { setMenuOpen(false); onSettings(); }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <IconSettings size={14} /> Settings
              </span>
            </button>
            <button type="button" role="menuitem" style={styles.userMenuBtn} onClick={onLogout}>
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}
